import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, MapPin, Wind, Droplets, Thermometer, Eye, Sun, Cloud, CloudRain, Snowflake, CloudLightning, Wind as WindIcon } from 'lucide-react';
import { fetchWeather, fetchForecast, getWeatherByLocation } from './services/WeatherService';
import WeatherVisuals from './components/WeatherVisuals';

// Determines the precise visual mood based on city time + weather condition
const getEffectiveMood = (dt, timezone, sunrise, sunset, apiMood) => {
  const isNight = dt < sunrise || dt > sunset;
  if (isNight) return 'night';

  const m = apiMood?.toLowerCase();
  if (m !== 'clear') return m; // rain, clouds, snow, etc. keep their mood

  // For clear sky, break into time-of-day sub-moods
  const localHour = new Date((dt + timezone) * 1000).getUTCHours();
  if (localHour >= 5 && localHour < 8)  return 'sunrise';
  if (localHour >= 8 && localHour < 16) return 'afternoon';
  if (localHour >= 16 && localHour < 20) return 'sunset';
  return 'sunset'; // edge case around golden hour
};

// Always white text — clean and consistent across all weather states
const getTheme = () => ({
  primary:    'text-white',
  secondary:  'text-white/90',
  muted:      'text-white/70',
  faint:      'text-white/50',
  glass:      'bg-white/10 backdrop-blur-md border border-white/20',
  glassHover: 'hover:bg-white/20',
  placeholder:'placeholder:text-white/40',
  input:      'text-white',
  accent:     'text-white',
  accentBg:   'bg-white/10',
  ring:       'focus-within:ring-white/40',
});

const WeatherIconComponent = ({ mood, className }) => {
  switch(mood?.toLowerCase()) {
    case 'clear':      return <Sun className={className} />;
    case 'clouds':     return <Cloud className={className} />;
    case 'rain':       return <CloudRain className={className} />;
    case 'drizzle':    return <CloudRain className={className} />;
    case 'snow':       return <Snowflake className={className} />;
    case 'thunderstorm': return <CloudLightning className={className} />;
    default:           return <Cloud className={className} />;
  }
};

function App() {
  const [query, setQuery] = useState('');
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleWeatherData = React.useCallback(async (data) => {
    setWeather(data);
    setLoading(false);
    setError(null);
    try {
      const forecastData = await fetchForecast(data.coord.lat, data.coord.lon);
      setForecast(forecastData);
    } catch (err) {
      console.error('Forecast failed:', err);
    }
  }, []);

  const handleSearch = React.useCallback(async (city) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWeather(city || query);
      handleWeatherData(data);
    } catch (err) {
      setError('City not found. Try again.');
      setLoading(false);
    }
  }, [query, handleWeatherData]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const data = await getWeatherByLocation(pos.coords.latitude, pos.coords.longitude);
            handleWeatherData(data);
          } catch { handleSearch('Mumbai'); }
        },
        () => handleSearch('Mumbai')
      );
    } else {
      handleSearch('Mumbai');
    }
  }, [handleSearch, handleWeatherData]);

  if (loading && !weather) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#0f172a]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-12 h-12 border-4 border-sky-400 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  // Compute the full effective mood (sunrise / afternoon / sunset / night / rain / etc.)
  const effectiveMood = weather
    ? getEffectiveMood(weather.dt, weather.timezone, weather.sys.sunrise, weather.sys.sunset, weather.weather[0].main)
    : 'afternoon';

  const tc = getTheme();

  return (
    <div className="relative min-h-screen w-full overflow-hidden font-sans">
      <WeatherVisuals mood={effectiveMood} />

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 py-10 md:py-16 flex flex-col items-center">

        {/* Search Bar */}
        <div className="w-full max-w-md mb-10">
          <div className={`relative ${tc.glass} ${tc.ring} rounded-2xl p-1 flex items-center group transition-all focus-within:ring-2`}>
            <Search className={`w-5 h-5 ml-4 transition-colors ${tc.muted} group-focus-within:${tc.accent}`} />
            <input
              type="text"
              placeholder="Search city..."
              className={`w-full bg-transparent border-none outline-none px-4 py-3 ${tc.input} ${tc.placeholder}`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            {error && <span className="absolute -bottom-7 left-4 text-xs text-red-400 font-medium">{error}</span>}
          </div>
        </div>

        {weather && (
          <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

            {/* Left Column */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              key={weather.name}
              className="lg:col-span-8 space-y-8"
            >
              <div className="flex flex-col md:flex-row items-start justify-between gap-6">
                <div>
                  <div className={`flex items-center gap-2 mb-2 ${tc.muted}`}>
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm font-semibold uppercase tracking-widest">
                      {weather.name}, {weather.sys.country}
                    </span>
                  </div>
                  <h1 className={`text-8xl md:text-[9rem] font-black tracking-tighter tabular-nums ${tc.primary} drop-shadow-sm`}>
                    {Math.round(weather.main.temp)}°
                  </h1>
                </div>

                <div className="text-left md:text-right mt-4">
                  <div className={`text-4xl md:text-5xl font-semibold capitalize mb-2 ${tc.primary}`}>
                    {weather.weather[0].description}
                  </div>
                  <div className={`text-lg ${tc.secondary}`}>
                    Feels like {Math.round(weather.main.feels_like)}°
                  </div>
                  <div className={`text-sm mt-1 ${tc.muted} capitalize`}>
                    {effectiveMood === 'sunrise' && '🌅 Golden Hour — Sunrise'}
                    {effectiveMood === 'afternoon' && '☀️ Bright Afternoon'}
                    {effectiveMood === 'sunset' && '🌇 Magic Hour — Sunset'}
                    {effectiveMood === 'night' && '🌙 Clear Night'}
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Droplets}    label="Humidity"    value={`${weather.main.humidity}%`}                          tc={tc} />
                <StatCard icon={WindIcon}    label="Wind"        value={`${Math.round(weather.wind.speed * 3.6)} km/h`}       tc={tc} />
                <StatCard icon={Thermometer} label="Pressure"    value={`${weather.main.pressure} hPa`}                       tc={tc} />
                <StatCard icon={Eye}         label="Visibility"  value={`${(weather.visibility / 1000).toFixed(1)} km`}       tc={tc} />
              </div>
            </motion.div>

            {/* Right Column: Forecast */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-4"
            >
              <div className={`${tc.glass} rounded-3xl p-6 h-full`}>
                <h3 className={`text-base font-bold mb-5 flex items-center gap-2 ${tc.primary}`}>
                  <Eye className={`w-4 h-4 ${tc.accent}`} /> Hourly Forecast
                </h3>
                <div className="space-y-1 max-h-[420px] overflow-y-auto pr-1">
                  {forecast.slice(0, 12).map((item, i) => (
                    <div key={i} className={`flex items-center justify-between px-3 py-2.5 rounded-2xl ${tc.glassHover} transition-colors group cursor-default`}>
                      <div className={`text-sm font-medium ${tc.muted} group-hover:${tc.secondary} transition-colors`}>
                        {new Date(item.dt * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div className="flex items-center gap-3">
                        <WeatherIconComponent mood={item.weather[0].main} className={`w-5 h-5 ${tc.accent}`} />
                        <span className={`font-bold ${tc.primary}`}>{Math.round(item.main.temp)}°</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

          </div>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, tc }) => (
  <div className={`${tc.glass} ${tc.glassHover} rounded-3xl p-5 flex flex-col items-center justify-center text-center group transition-all cursor-default`}>
    <div className={`w-11 h-11 rounded-2xl ${tc.accentBg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
      <Icon className={`w-5 h-5 ${tc.accent}`} />
    </div>
    <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${tc.faint}`}>{label}</div>
    <div className={`text-xl font-bold ${tc.primary}`}>{value}</div>
  </div>
);

export default App;